/*
 * @nt/ui/date-range — the date math behind the date pickers.
 *
 * Every date is a `YYYY-MM-DD` string (the URL form) and every month a
 * `YYYY-MM` string. Arithmetic runs on UTC midnights, so a daylight-saving
 * switch or the viewer's offset can never move a day. Only `todayIso` reads
 * the clock, and it reads the viewer's LOCAL calendar day: "today" is the day
 * on the wall where the user sits. Weeks start on Monday.
 */

export type IsoDate = string;
export type IsoMonth = string;

/** An inclusive range. An empty end is open; both empty is all time. */
export interface DateRange {
  from: IsoDate;
  to: IsoDate;
}

/** Every preset the pickers can resolve. */
export type DatePreset =
  | "today"
  | "yesterday"
  | "last_7_days"
  | "last_30_days"
  | "this_week"
  | "last_week"
  | "this_month"
  | "last_month"
  | "this_quarter"
  | "last_quarter"
  | "this_year"
  | "year_to_date"
  | "last_year"
  | "all_time";

export type DateRangeChoice = DatePreset | "custom";

/** The default preset menu, top to bottom. `this_year` is opt-in. */
export const DATE_RANGE_PRESETS: readonly DatePreset[] = [
  "today",
  "yesterday",
  "last_7_days",
  "last_30_days",
  "this_week",
  "last_week",
  "this_month",
  "last_month",
  "this_quarter",
  "last_quarter",
  "year_to_date",
  "last_year",
  "all_time",
];

export const DATE_PRESET_LABEL: Record<DateRangeChoice, string> = {
  today: "Today",
  yesterday: "Yesterday",
  last_7_days: "Last 7 days",
  last_30_days: "Last 30 days",
  this_week: "This week",
  last_week: "Last week",
  this_month: "This month",
  last_month: "Last month",
  this_quarter: "This quarter",
  last_quarter: "Last quarter",
  this_year: "This year",
  year_to_date: "Year to date",
  last_year: "Last year",
  all_time: "All time",
  custom: "Custom",
};

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;
const MONTHS_LONG = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;
const WEEKDAYS_LONG = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const;
/** Column heads for a Monday-first grid. */
export const WEEKDAY_SHORT = [
  "Mo",
  "Tu",
  "We",
  "Th",
  "Fr",
  "Sa",
  "Su",
] as const;

const pad = (n: number, width = 2) => String(n).padStart(width, "0");

function parts(date: IsoDate): [number, number, number] {
  const [y, m, d] = date.split("-").map(Number);
  return [y ?? 0, m ?? 0, d ?? 0];
}

function fromUtc(ms: number): IsoDate {
  const date = new Date(ms);
  return `${pad(date.getUTCFullYear(), 4)}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}

function utc(date: IsoDate): number {
  const [y, m, d] = parts(date);
  return Date.UTC(y, m - 1, d);
}

/** A real calendar day written `YYYY-MM-DD` (no 30 February). */
export function isIsoDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [y, m, d] = parts(value);
  if (m < 1 || m > 12 || d < 1) return false;
  return d <= lastDayOfMonth(y, m);
}

function lastDayOfMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** The viewer's calendar day. */
export function todayIso(now: Date = new Date()): IsoDate {
  return `${pad(now.getFullYear(), 4)}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export function addDays(date: IsoDate, days: number): IsoDate {
  return fromUtc(utc(date) + days * 86_400_000);
}

/** 0 = Monday … 6 = Sunday. */
export function weekdayIndex(date: IsoDate): number {
  return (new Date(utc(date)).getUTCDay() + 6) % 7;
}

export function startOfWeek(date: IsoDate): IsoDate {
  return addDays(date, -weekdayIndex(date));
}

export function monthOf(date: IsoDate): IsoMonth {
  return date.slice(0, 7);
}

export function shiftMonth(month: IsoMonth, by: number): IsoMonth {
  const [y, m] = month.split("-").map(Number) as [number, number];
  const index = y * 12 + (m - 1) + by;
  return `${pad(Math.floor(index / 12), 4)}-${pad((index % 12) + 1)}`;
}

/** First and last day of a month. */
function monthRange(month: IsoMonth): DateRange {
  const [y, m] = month.split("-").map(Number) as [number, number];
  return { from: `${month}-01`, to: `${month}-${pad(lastDayOfMonth(y, m))}` };
}

/**
 * Six Monday-first weeks covering the month: always 42 days, so a calendar
 * never grows or shrinks a row as the user pages through months.
 */
export function monthGrid(month: IsoMonth): IsoDate[][] {
  const start = startOfWeek(`${month}-01`);
  return Array.from({ length: 6 }, (_, week) =>
    Array.from({ length: 7 }, (_, day) => addDays(start, week * 7 + day)),
  );
}

export function compareDates(a: IsoDate, b: IsoDate): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** The dates a preset names, on a calendar year. `all_time` is open both ways. */
export function resolveDatePreset(
  preset: DatePreset,
  today: IsoDate,
): DateRange {
  const [year, month] = parts(today);
  const thisMonth = monthOf(today);
  switch (preset) {
    case "today":
      return { from: today, to: today };
    case "yesterday": {
      const day = addDays(today, -1);
      return { from: day, to: day };
    }
    case "last_7_days":
      return { from: addDays(today, -6), to: today };
    case "last_30_days":
      return { from: addDays(today, -29), to: today };
    case "this_week": {
      const monday = startOfWeek(today);
      return { from: monday, to: addDays(monday, 6) };
    }
    case "last_week": {
      const monday = addDays(startOfWeek(today), -7);
      return { from: monday, to: addDays(monday, 6) };
    }
    case "this_month":
      return monthRange(thisMonth);
    case "last_month":
      return monthRange(shiftMonth(thisMonth, -1));
    case "this_quarter":
    case "last_quarter": {
      const first = shiftMonth(
        `${pad(year, 4)}-01`,
        Math.floor((month - 1) / 3) * 3 - (preset === "last_quarter" ? 3 : 0),
      );
      return { from: `${first}-01`, to: monthRange(shiftMonth(first, 2)).to };
    }
    case "this_year":
      return { from: `${pad(year, 4)}-01-01`, to: `${pad(year, 4)}-12-31` };
    case "year_to_date":
      return { from: `${pad(year, 4)}-01-01`, to: today };
    case "last_year":
      return {
        from: `${pad(year - 1, 4)}-01-01`,
        to: `${pad(year - 1, 4)}-12-31`,
      };
    case "all_time":
      return { from: "", to: "" };
  }
}

/** The first preset in `presets` that names exactly this range, else custom. */
export function matchDatePreset(
  range: DateRange,
  today: IsoDate,
  presets: readonly DatePreset[] = DATE_RANGE_PRESETS,
): DateRangeChoice {
  for (const preset of presets) {
    const resolved = resolveDatePreset(preset, today);
    if (resolved.from === range.from && resolved.to === range.to) return preset;
  }
  return "custom";
}

/** "Oct 7, 2026" */
export function formatDate(date: IsoDate): string {
  const [y, m, d] = parts(date);
  return `${MONTHS[m - 1]} ${d}, ${y}`;
}

/** "October 2026" */
export function formatMonth(month: IsoMonth): string {
  const [y, m] = month.split("-").map(Number) as [number, number];
  return `${MONTHS_LONG[m - 1]} ${y}`;
}

/** "Wednesday, October 7, 2026": what a screen reader hears for a day. */
export function formatDateLong(date: IsoDate): string {
  const [y, m, d] = parts(date);
  return `${WEEKDAYS_LONG[weekdayIndex(date)]}, ${MONTHS_LONG[m - 1]} ${d}, ${y}`;
}

/** A range, shortened by what its two ends share. */
export function formatDateRange(range: DateRange): string {
  const { from, to } = range;
  if (!from && !to) return "All time";
  if (!to) return `From ${formatDate(from)}`;
  if (!from) return `Until ${formatDate(to)}`;
  if (from === to) return formatDate(from);
  const [fy, fm, fd] = parts(from);
  const [ty, tm, td] = parts(to);
  if (fy === ty && fm === tm) return `${MONTHS[fm - 1]} ${fd} – ${td}, ${fy}`;
  if (fy === ty)
    return `${MONTHS[fm - 1]} ${fd} – ${MONTHS[tm - 1]} ${td}, ${fy}`;
  return `${formatDate(from)} – ${formatDate(to)}`;
}

/**
 * What a trigger shows: "Last month · Sep 1 – 30, 2026", or just the dates for
 * a custom range. `choice` wins when the caller knows which preset was picked.
 */
export function rangeLabel(
  range: DateRange,
  today: IsoDate,
  presets: readonly DatePreset[] = DATE_RANGE_PRESETS,
  choice?: DateRangeChoice,
): string {
  const named = choice ?? matchDatePreset(range, today, presets);
  if (named === "all_time") return DATE_PRESET_LABEL.all_time;
  const dates = formatDateRange(range);
  return named === "custom" ? dates : `${DATE_PRESET_LABEL[named]} · ${dates}`;
}

/** A typed date: `YYYY-MM-DD` (or with slashes, unpadded). Null when it is not a real day. */
export function parseTypedDate(text: string): IsoDate | null {
  const match = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/.exec(text.trim());
  if (!match) return null;
  const date = `${match[1]}-${pad(Number(match[2]))}-${pad(Number(match[3]))}`;
  return isIsoDate(date) ? date : null;
}
